import { useCallback, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

const TOKEN_KEY = "chystotil-auth-token";

/**
 * Password-only auth. On successful login the server returns a session token
 * which is kept in localStorage, so a page refresh doesn't ask for the
 * password again.
 */
export function useAuth() {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const me = useQuery(api.auth.me, { token: token ?? undefined });
  const loginMutation = useMutation(api.auth.login);
  const logoutMutation = useMutation(api.auth.logout);

  const login = useCallback(
    async (password: string) => {
      const { token: newToken } = await loginMutation({ password });
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
    },
    [loginMutation]
  );

  const logout = useCallback(async () => {
    const current = token;
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    if (current) await logoutMutation({ token: current });
  }, [token, logoutMutation]);

  return {
    role: me?.role ?? null,
    isAdmin: me?.role === "admin",
    // A stored token is being validated on the server
    isLoading: token !== null && me === undefined,
    login,
    logout,
  };
}
